import { useCallback, useId, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ZodError } from 'zod'
import type { CommandKey, PayloadOf } from '@gk/contracts'
import { stepUp } from '../auth/stepUp.ts'
import { useClub, useEngineState } from '../club/ClubProvider.tsx'
import { AdminError } from '../lib/adminApi.ts'
import { localOnlyTables } from '../sync/db.ts'
import type { EnqueueOptions } from '../sync/outbox.ts'
import { settleTransient } from '../sync/transient.ts'
import { TransportError } from '../sync/transport.ts'
import { Banner, Field, errorText } from './components.tsx'

/** `api.begin_online_action` and the RPCs refuse with the business code as the message (AAL2_REQUIRED, MFA_FACTOR_UNAPPROVED, ...). */
export const businessCode = (e: unknown): string | null => (e instanceof Error && /^[A-Z][A-Z0-9_]{2,}$/.test(e.message) ? e.message : null)

/** One fresh authenticator code covers every Y* action for ten minutes (the server checks the proof is that recent); it is asked again after nine. */
let lastStepUp = 0
const FRESH_MS = 9 * 60_000

/** The Y* actions of §6.3 (members, factors, devices, overrides, pay, contacts) and the privileged reads: online only, after a fresh second-factor
 *  code, with a server nonce for a command. Everything here is a REQUEST; the server decides. */
export function useSensitive() {
  const { t } = useTranslation()
  const { sendAndWait, beginOnline, db, transport } = useClub()
  const online = useEngineState().online !== false
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ok, setOk] = useState<string | null>(null)
  const [fresh, setFresh] = useState(Date.now() - lastStepUp < FRESH_MS)
  // never leave an online-only command waiting: no answer in time discards it, and a sheet, a pay rate or a contact goes from the device at once (sync/transient.ts)
  const discard = useCallback((id: string) => db.transaction('rw', [db.outbox, db.overlay, ...localOnlyTables(db)], async () => {
    await db.outbox.update(id, { status: 'discarded' }); await db.overlay.where('command_id').equals(id).delete(); await settleTransient(db, id)
  }), [db])

  const prove = useCallback(async (): Promise<boolean> => {
    if (Date.now() - lastStepUp < FRESH_MS) return true
    // the proof has expired (or was never made here): the field must show again, also where it said "recent"
    if (!/^\d{6}$/.test(code.replace(/\s/g, ''))) { setFresh(false); setError(t('sensitive.codeNeeded')); return false }
    const s = await stepUp(code)
    if (s.error) { setError(s.error === 'no_factor' ? t('release.noFactor') : t('auth.mfaInvalid')); return false }
    lastStepUp = Date.now(); setFresh(true); setCode('')
    return true
  }, [code, t])

  const wrap = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    setBusy(true); setError(null); setOk(null)
    try {
      if (!(await prove())) return undefined
      return await fn()
    } catch (e) {
      const c = e instanceof AdminError ? e.code : businessCode(e)
      if (c === 'MFA_STEP_UP_REQUIRED' || c === 'AAL2_REQUIRED') { lastStepUp = 0; setFresh(false) }
      setError(c ? errorText(t, c) : e instanceof ZodError ? e.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join(' · ') : e instanceof TransportError && e.kind === 'network' ? t('errors.network') : t('errors.unknown'))
      return undefined
    } finally { setBusy(false) }
  }, [prove, t])

  /** An online-only command: nonce, send, wait for the SERVER's verdict. Resolves true when applied. */
  const command = useCallback(<K extends CommandKey>(key: K, payload: PayloadOf<K>, successKey: string, opts?: EnqueueOptions) => wrap(async () => {
    const nonce = await beginOnline()
    const v = await sendAndWait(key, payload, { ...opts, nonce })
    if (v.status === 'queued') { await discard(v.commandId); setError(t('errors.network')); return false }
    if (v.status === 'applied' || v.status === 'applied_with_exception') { setOk(t(successKey)); return true }
    setError(errorText(t, v.code)); return false
  }), [wrap, beginOnline, sendAndWait, discard, t])

  /** `command`, for a screen that shows what the server answered (M20: a departure lists the planned jobs to give to someone else): the handler's result when
   *  applied, `undefined` otherwise (the refusal is shown as for `command`). */
  const commandResult = useCallback(<K extends CommandKey>(key: K, payload: PayloadOf<K>, successKey: string, opts?: EnqueueOptions) => wrap(async () => {
    const nonce = await beginOnline()
    const v = await sendAndWait(key, payload, { ...opts, nonce })
    if (v.status === 'queued') { await discard(v.commandId); setError(t('errors.network')); return undefined }
    if (v.status === 'applied' || v.status === 'applied_with_exception') { setOk(t(successKey)); return v.result ?? {} }
    setError(errorText(t, v.code)); return undefined
  }), [wrap, beginOnline, sendAndWait, discard, t])

  /** A privileged RPC (a read or a one-time secret): the same proof, no nonce. */
  const call = useCallback(<T,>(fn: string, args: Record<string, unknown>) => wrap(() => transport.rpc<T>(fn, args)), [wrap, transport])

  // one proof covers every section of a page (M20: the register's detail has several): a field mounted before another one's proof says "recent" on its next render
  return { code, setCode, busy, error, ok, online, fresh: fresh || Date.now() - lastStepUp < FRESH_MS, command, commandResult, call, wrap, setError, setOk, reset: () => { setError(null); setOk(null) } }
}
export type Sensitive = ReturnType<typeof useSensitive>

/** Asks for the authenticator code only when the last proof is older than nine minutes. */
export function StepUpField({ s, children }: { s: Sensitive; children?: ReactNode }) {
  const { t } = useTranslation()
  const id = useId()                                                    // a page may hold several (one per section of a person's detail): each label names its own field
  return (
    <div className="flex flex-col gap-3" data-testid="stepup">
      {!s.online && <Banner tone="warn">{t('common.onlineOnly')}</Banner>}
      {s.fresh ? <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300" data-testid="stepup-fresh">✓ {t('sensitive.fresh')}</p> : (
        <Field label={t('release.code')} hint={t('sensitive.codeHelp')} htmlFor={id}>
          <input id={id} className="input text-center text-2xl tracking-widest" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={s.code} onChange={(e) => s.setCode(e.target.value)} data-testid="stepup-code" />
        </Field>
      )}
      {children}
      {s.error && <Banner tone="error"><span data-testid="sensitive-error">{s.error}</span></Banner>}
      {s.ok && <Banner tone="ok"><span data-testid="sensitive-ok">{s.ok}</span></Banner>}
    </div>
  )
}
